import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { styled } from "@mui/styles";
import { v4 as uuidv4 } from "uuid";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { FileRejection, FileWithPath } from "react-dropzone";
import UploadDropZone from "controls/global/upload-files/UploadDropZone";
import UploadStatusGrid from "controls/global/upload-files/UploadStatusGrid";
import ConfirmationDialogWithProgressbar from "controls/global/dialogs/confirmation-dialog-with-progressbar";
import FlashMessage from "controls/global/flash-message/FlashMessage";
import { useProcessStatusTracking } from "utils/context/ProcessStatusContext";
import { useUploadDropZoneActions } from "utils/context/UploadDropZoneContext";
import { useFlashMessageActions } from "utils/context/FlashMessageContext";
import useFlashMessage from "utils/custom-hooks/useFlashMessage";
import { FlashMessageType, ProgressImageType, UIConstants } from "utils/data/enum";
import { FileUploadAdditionalInfo, InventoryVerifyStatus } from "entities/UIModel";
import {
  getFormattedErrorMessage,
  mapInventoryVerifyResponse,
  mapRejectedFiles,
} from "pages/lookup/inventory-verify/InventoryVerifyHelper";
import { uploadPremiumReceivableDocument } from "utils/api/premiumReceivableApi";
import {
  StyledDialog,
  StyledDialogTitle,
  IconContainer,
  CloseIconContainer,
  StyledDialogContent,
} from "../shared/SharedDialogStyles";
import {
  borderRadius,
  colors,
  fontSize,
  fontWeight,
  gaps,
  margin,
  padding,
} from "theme/defaultStyle";

interface UploadAccrualsAdjustmentsModalProps {
  open: boolean;
  onClose: () => void;
}

type DocumentTypeCode = "Adjustment" | "Accrual";

const InfoBanner = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  gap: gaps.small1,
  backgroundColor: colors.blue19,
  borderRadius: borderRadius.medium,
  padding: padding.medium1,
  "& .MuiSvgIcon-root": {
    color: colors.blue15,
    flexShrink: 0,
    marginTop: "2px",
  },
});

const InfoText = styled(Typography)({
  fontSize: fontSize.small,
  color: colors.blue15,
  "& b": {
    fontWeight: fontWeight.bold2,
  },
});

const FailedUploadHeader = styled(Box)({
  marginTop: margin.xlarge1,
  marginBottom: margin.large,
  fontWeight: fontWeight.bold1,
  fontSize: fontSize.large,
});

const PartialWarningBanner = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  gap: gaps.small1,
  backgroundColor: "#fff8e5",
  border: "1px solid #e8d580",
  borderRadius: borderRadius.medium,
  padding: padding.medium1,
  marginTop: margin.xlarge1,
  "& .MuiSvgIcon-root": {
    color: "#8a6d1f",
    flexShrink: 0,
    marginTop: "2px",
  },
});

const PartialWarningText = styled(Typography)({
  fontSize: fontSize.small,
  color: "#6b530f",
  "& b": {
    fontWeight: fontWeight.bold2,
  },
});

const UPLOAD_SUCCESS_MESSAGE = "File uploaded successfully!";
const UPLOAD_ERROR_MESSAGE_HEADER = "Upload Failed!";
const UPLOAD_ERROR_MESSAGE_DETAIL = "Please try again.";

// Backend messages look like "5 of 708 rows failed to process." — when the
// failed count is less than the total, the file itself uploaded fine and
// most rows processed; only a subset need attention. Surfacing that as a
// hard failure (red, "Failed to Upload") reads as if nothing was saved.
const ROWS_FAILED_PATTERN = /(\d+)\s+of\s+(\d+)\s+rows failed/i;

function getRowFailureCounts(message?: string): { failed: number; total: number } | null {
  if (!message) return null;
  const match = message.match(ROWS_FAILED_PATTERN);
  if (!match) return null;
  return { failed: Number(match[1]), total: Number(match[2]) };
}

function isPartialRowFailure(status: InventoryVerifyStatus): boolean {
  const counts = getRowFailureCounts(status.errorMessage);
  return !!counts && counts.failed > 0 && counts.failed < counts.total;
}

// Backend endpoint: POST /api/Document/UploadPremiumReceivable
const UploadAccrualsAdjustmentsModal = ({
  open,
  onClose,
}: UploadAccrualsAdjustmentsModalProps) => {
  const [documentTypeCode, setDocumentTypeCode] =
    useState<DocumentTypeCode>("Adjustment");
  // UploadDropZone memoizes its onDrop handler once (empty deps) and never
  // rebinds it, so handleOnUpload's closure would otherwise always see the
  // documentTypeCode value from the modal's first render. A ref sidesteps
  // that without touching the shared component.
  const documentTypeCodeRef = useRef<DocumentTypeCode>(documentTypeCode);
  useEffect(() => {
    documentTypeCodeRef.current = documentTypeCode;
  }, [documentTypeCode]);

  const [uploadCompleted, setUploadCompleted] = useState<boolean>(false);
  const [showUploadError, setShowUploadError] = useState<boolean>(false);
  const [uploadStatusList, setUploadStatusList] = useState<InventoryVerifyStatus[]>([]);
  const [partialStatusList, setPartialStatusList] = useState<InventoryVerifyStatus[]>([]);

  const isProgressCompletedRef = useRef<boolean>(false);

  // progress dialog
  const [, { setBypassDefaultLoader, setPercentageComplete }] = useProcessStatusTracking();
  const [openProgressDialog, setOpenProgressDialog] = useState<boolean>(false);
  const [runProgress, setRunProgress] = useState<boolean>(false);
  const [progressRequestId, setProgressRequestId] = useState<string>("");

  // drop zone + flash message
  const [, { setDisabled, setVisble }] = useUploadDropZoneActions();
  const [, { showFlashMessage, closeFlashMessage }] = useFlashMessageActions();
  useFlashMessage();

  const handleOnCloseProgressDialog = async () => {
    isProgressCompletedRef.current = true;
    if (uploadCompleted) {
      setOpenProgressDialog(false);
      setRunProgress(false);
      setProgressRequestId("");
    }
  };

  const handleClose = () => {
    setDocumentTypeCode("Adjustment");
    setShowUploadError(false);
    setUploadStatusList([]);
    setPartialStatusList([]);
    onClose();
  };

  const upload = async (
    acceptedFiles: FileWithPath[],
    rejectedFiles: FileRejection[]
  ) => {
    setUploadCompleted(false);
    const requestId = uuidv4();
    setProgressRequestId(requestId);
    setBypassDefaultLoader(true);
    setOpenProgressDialog(true);
    setRunProgress(true);
    isProgressCompletedRef.current = false;

    const fileAdditionalInfos: FileUploadAdditionalInfo[] = [];
    const formData = new FormData();

    let index = 0;
    acceptedFiles?.forEach((file) => {
      formData.append("files", file, file.name);
      fileAdditionalInfos.push({
        index,
        filePath: file.path || "",
        fileName: file.name,
        fileSize: file.size,
        errorMessage: "",
        isValidFile: true,
      });
      index++;
    });
    rejectedFiles?.forEach((rejectedFile) => {
      fileAdditionalInfos.push({
        index,
        filePath: "",
        fileName: rejectedFile.file.name,
        fileSize: rejectedFile.file.size,
        errorMessage: getFormattedErrorMessage(rejectedFile.errors),
        isValidFile: false,
      });
      index++;
    });

    formData.append("fileUploadAdditionalInfo", JSON.stringify(fileAdditionalInfos));

    const result = await uploadPremiumReceivableDocument(
      requestId,
      documentTypeCodeRef.current.toUpperCase() as "ADJUSTMENT" | "ACCRUAL",
      formData
    );

    if (!result) {
      showFlashMessage(
        FlashMessageType.Error,
        UPLOAD_ERROR_MESSAGE_HEADER,
        UPLOAD_ERROR_MESSAGE_DETAIL
      );
    } else {
      const statusList = mapInventoryVerifyResponse(result);
      const failedList = (statusList || []).filter((s) => !s.isUploadSuccess);

      if (failedList.length === 0) {
        showFlashMessage(FlashMessageType.Success, UPLOAD_SUCCESS_MESSAGE, "");
        handleClose();
      } else {
        // A file with "X of Y rows failed" (X < Y) mostly succeeded - keep it
        // out of the red "Failed to Upload" bucket so the UI doesn't read as
        // a total failure when most rows were saved.
        setPartialStatusList(failedList.filter(isPartialRowFailure));
        setUploadStatusList(failedList.filter((s) => !isPartialRowFailure(s)));
        setShowUploadError(true);
      }
    }

    setPercentageComplete(requestId);
    setUploadCompleted(true);
  };

  const handleOnUpload = async (
    acceptedFiles: FileWithPath[],
    rejectedFiles: FileRejection[]
  ) => {
    setShowUploadError(false);
    setUploadStatusList([]);
    setPartialStatusList([]);

    if (acceptedFiles?.length > 0) {
      setDisabled(true);
      setVisble(false);
      await upload(acceptedFiles, rejectedFiles);
      setDisabled(false);
      setVisble(true);
    } else {
      setUploadStatusList(mapRejectedFiles(rejectedFiles));
      setShowUploadError(true);
    }
  };

  const handleFlashMessageClose = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    closeFlashMessage();
  };

  useEffect(() => {
    setShowUploadError(false);
    setUploadStatusList([]);
    setPartialStatusList([]);
    isProgressCompletedRef.current = false;
  }, [open]);

  useEffect(() => {
    if (uploadCompleted && openProgressDialog) {
      handleOnCloseProgressDialog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadCompleted]);

  return (
    <>
      <StyledDialog open={open}>
        <StyledDialogTitle>
          <IconContainer>
            <UploadFileOutlinedIcon />
          </IconContainer>
          <Typography>Upload Accruals / Adjustments</Typography>
          <CloseIconContainer>
            <IconButton className="iconButton" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </CloseIconContainer>
        </StyledDialogTitle>

        <StyledDialogContent>
          <InfoBanner>
            <InfoOutlinedIcon />
            {documentTypeCode === "Adjustment" ? (
              <InfoText>
                Upload a CSV with the standard adjustment schema. Required
                columns: <b>AgentID, Accounting Period, Received Date
                (MMDDYYYY), PolicyDate (MMYYYY), Number of Policies, Liability
                Amount, SI Liability Amount, Gross All Inclusive, Remittance
                Basis, Underwriter Retention</b>. Optional: Number of SI
                Policies, Comments.
              </InfoText>
            ) : (
              <InfoText>
                Upload a CSV with the standard accrual schema. Required
                columns: <b>Agent ID, No of Policies, No of SI Policies,
                Liability Amount, SI Liability Amount, Gross All Inclusive,
                Remittance Basis, Retention, Accural Net Premium, Covering
                From Date (MMYYYY), Covering To Date (MMYYYY)</b>.
              </InfoText>
            )}
          </InfoBanner>

          <RadioGroup
            row
            value={documentTypeCode}
            onChange={(_e, v) => setDocumentTypeCode(v as DocumentTypeCode)}
            sx={{ gap: gaps.medium1 }}
          >
            <FormControlLabel
              value="Adjustment"
              control={<Radio size="small" sx={{ padding: 0, marginRight: gaps.xsmall1 }} />}
              label="Adjustment"
              sx={{ marginLeft: 0, marginRight: 0 }}
            />
            <FormControlLabel
              value="Accrual"
              control={<Radio size="small" sx={{ padding: 0, marginRight: gaps.xsmall1 }} />}
              label="Accrual"
              sx={{ marginLeft: 0, marginRight: 0 }}
            />
          </RadioGroup>

          <UploadDropZone handleOnUpload={handleOnUpload} />

          {partialStatusList.length > 0 && (
            <>
              <PartialWarningBanner>
                <WarningAmberOutlinedIcon />
                <PartialWarningText>
                  The file was uploaded and most rows processed successfully, but{" "}
                  <b>some rows need review</b> before they&apos;ll count toward the ledger.
                  Fix and re-upload just the affected rows.
                </PartialWarningText>
              </PartialWarningBanner>
              <UploadStatusGrid
                className="partial-status-grid"
                fileUploadStatuses={partialStatusList}
                severity="warning"
              />
            </>
          )}

          {showUploadError && uploadStatusList.length > 0 && (
            <>
              <FailedUploadHeader>Failed to Upload</FailedUploadHeader>
              <UploadStatusGrid
                className="failed-status-only-grid"
                fileUploadStatuses={uploadStatusList}
              />
            </>
          )}
        </StyledDialogContent>
      </StyledDialog>

      <ConfirmationDialogWithProgressbar
        autoClose={true}
        title="Please wait while uploading your file ..."
        closeButtonText="Done! Click to Continue"
        isOpen={openProgressDialog}
        onClose={handleOnCloseProgressDialog}
        requestId={progressRequestId}
        runProgress={runProgress}
        progressImageType={ProgressImageType.circular}
      />

      <FlashMessage
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        autoHideDuration={Number(UIConstants.FLASH_MESSAGE_AUTO_HIDDEN_DURATION)}
        onClose={handleFlashMessageClose}
      />
    </>
  );
};

export default UploadAccrualsAdjustmentsModal;
